import React, { useEffect, useMemo, useState } from "react"

import dayjs from 'dayjs';

import { useMutation } from "@tanstack/react-query";

import { useForm, useWatch } from "antd/lib/form/Form";
import { Button, Col, Empty, Flex, FormInstance, notification, Spin, Tooltip } from "antd";
import { Form, Input, Row } from "antd";
import { Divider } from "antd";
import { QuestionCircleTwoTone } from "@ant-design/icons";

import { EscolaService } from "../../../../Services/Domain/Pessoa/EscolaService";
import { FormularioDeContatos } from "../../../../Components/Formularios/FormularioDeContatos";
import { FormularioDeEnderecos } from "../../../../Components/Formularios/FormularioDeEnderecos";
import { PropriedadesDeNotificacao } from "../../../../Utils/Notificacoes";
import { TypeEmail } from "../../../../Models/Pessoa/Contato/Email";
import { TypeEndereco } from "../../../../Models/Pessoa/Endereco";
import { TypeEscola } from "../../../../Models/Domain/Pessoa/Escola";
import { TypeTelefone } from "../../../../Models/Pessoa/Contato/Telefone";
import { TypeUsuario } from "../../../../Models/Usuario";
import { scrollarParaOElemento } from "../../../../Utils/Navegacao"

import { Formatacoes, Sanitizacoes } from "../../../../Utils/Formatacao";
import { InputComReactIMask } from "../../../../Components/InputComReactIMask";
import InputDate from "../../../../Components/Base/Input/InputDate";

const PERMITIR_CRIACAO_DE_USUARIO_DA_AREA_RESTRITA = true;

export type TypeDadosDeAcesso = {
    usuario: TypeUsuario["usuario"],
    senha: TypeUsuario["senha"],
}

const FormularioDeDadosBasicosDaEscola: React.FC<{
    form: FormInstance<TypeEscola>,
    privilegio: "administrador" | "escola",
    depoisDeSubmeter?: (escola: TypeEscola) => any,
}> = (props) => {

    return <React.Fragment>
        <Form form={props.form} onFinish={props.depoisDeSubmeter} layout='vertical' autoComplete='off' >
            <Row gutter={16} >
                <Col xs={24} md={12}>
                    <Form.Item<TypeEscola> name={["pessoa", "nome"]} rules={[{ required: true, message: 'Por favor, informe a razão social!' }]} label='Razão Social'>
                        <Input placeholder="Informe a razão social"/>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item<TypeEscola> name={["pessoa", "nomeSocial"]} rules={[{ required: true, message: 'Por favor, informe o nome fantasia!' }]} label='Nome Fantasia'>
                        <Input placeholder="Informe o nome fantasia"/>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} md={6}>
                    <Tooltip 
                        title={props.privilegio != "administrador" ? <>Contate um administrador caso deseje modificar o CNPJ</> : undefined}
                        placement="bottomLeft"
                    >
                        <Form.Item<TypeEscola> name={["pessoa", "cnpj"]} rules={[
                            { required: true, message: 'Por favor, informe o CNPJ!'},
                            { pattern: /^[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}\/?[0-9]{4}-?[0-9]{2}$/, message: "Formato do CNPJ inválido" }
                        ]} label='CNPJ'>
                            <InputComReactIMask disabled={props.privilegio != "administrador"} maskOptions={{ mask: "00.000.000/0000-00" }} />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} md={6}>
                    <Form.Item<TypeEscola> name={["pessoa", "dataRegistro" ]} label='Data de Registro' >
                        <InputDate style={{ width: '100%' }} placeholder="Selecione a data" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                    <Form.Item<TypeEscola> name={["pessoa", "inscricaoEstadual" ]} label='Inscrição Estadual'>
                        <Input placeholder="Informe a inscrição estadual"/>
                    </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                    <Form.Item<TypeEscola> name={["pessoa", "inscricaoMunicipal" ]} label='Inscrição Municipal'>
                        <Input placeholder="Informe a incrição municipal" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item<TypeEscola> name={["pessoa", "site" ]}
                        label='Site'
                    >
                        <Input addonBefore="https://" placeholder="Informe o site" />
                    </Form.Item>
                </Col>

                {
                    props.privilegio == "escola" &&
                    <Col xs={24} md={12}>
                        <Tooltip
                            title={<>O nome de usuário utilizado para o acesso aos sistema. <br /> Consulte um administrador para alterá-lo.</>}
                            placement="bottomLeft"
                        >

                            <Form.Item<TypeEscola> name={["usuarioDaAreaRestrita", "usuario"]} label="Usuário">
                                <Input disabled placeholder="Informe o nome de usuário" />
                            </Form.Item>
                        </Tooltip>
                    </Col>
                }
            </Row>
        </Form>

    </React.Fragment>

}

export const CadastroDeEscola: React.FC<
    ({privilegio: "administrador", escolaEmAlteracao?: TypeEscola} | {privilegio: "escola", escolaEmAlteracao: TypeEscola} ) &
    {
        aoCancelar?: () => any,
        depoisDeFinalizar?: (codigoGerado?: number) => any,

        exibirNotificacaoAoFinalizar?: boolean 
    }
> = (props) => {
    const estaEditando = useMemo(() => props.escolaEmAlteracao != undefined, [props.escolaEmAlteracao]);

    const [enderecos, setEnderecos] = useState<TypeEndereco[]>(props.escolaEmAlteracao?.pessoa?.enderecos ?? []);
    const [telefones, setTelefones] = useState<TypeTelefone[]>(props.escolaEmAlteracao?.pessoa?.telefones ?? []);
    const [emails, setEmails] = useState<TypeEmail[]>(props.escolaEmAlteracao?.pessoa?.emails ?? []);

    const [formularioDeDadosDeAcesso] = useForm<TypeDadosDeAcesso>();
    const [formularioDeDadosBasicos] = useForm<TypeEscola>();
    const valoresDoFormularioDeDadosBasicos = useWatch([], formularioDeDadosBasicos);

    const [configurouAsDependenciasDeEdicao, setConfigurouAsDependenciasDeEdicao] = useState<boolean>(false);
    useEffect(() => {
        formularioDeDadosBasicos.setFieldsValue({
             ...props.escolaEmAlteracao,
            pessoa: {
                ...props.escolaEmAlteracao?.pessoa,
                dataRegistro: props.escolaEmAlteracao?.pessoa?.dataRegistro != undefined ? dayjs(props.escolaEmAlteracao.pessoa?.dataRegistro,
                    'YYYY-MM-DD')
                    : undefined
            }
        })

        setConfigurouAsDependenciasDeEdicao(true);
    }, [])

    const inserirEscola = useMutation({
        mutationFn: (escola: TypeEscola) => new EscolaService().inserir(escola),
        onSuccess: () => {
            if(props.exibirNotificacaoAoFinalizar != false){
                let mensagem = PERMITIR_CRIACAO_DE_USUARIO_DA_AREA_RESTRITA ?
                    <> Cadastro realizado com sucesso. <br /> Um usuário da área restrita foi criado para a escola em questão. </>
                    :
                    <> Cadastro realizado com sucesso. </>

                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso(mensagem)
                })
            }

            if (props.depoisDeFinalizar) props.depoisDeFinalizar();
        },
    });

    const atualizarEscola = useMutation({
        mutationFn: (cliente: TypeEscola) => new EscolaService().atualizar(cliente),
        onSuccess: () => {
            if(props.exibirNotificacaoAoFinalizar != false){
                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso('Cadastro atualizado com sucesso.')
                })
            }

            if (props.depoisDeFinalizar) props.depoisDeFinalizar();

        },
    });

    const aoFinalizar = async () => {
        formularioDeDadosBasicos.validateFields()
            .then((dadosBasicos) => {
                let payload: TypeEscola = ({
                    pessoa: {
                        ...dadosBasicos.pessoa,
                        cnpj: Formatacoes.somenteDigitos(dadosBasicos.pessoa?.cnpj),
                        enderecos, emails, telefones
                    }
                })

                if (estaEditando) {
                    payload = { ...props.escolaEmAlteracao, ...payload }
                    
                    if(props.privilegio == "escola"){
                        // payload.usuarioDaAreaRestrita = dadosBasicos.usuarioDaAreaRestrita
                        payload.usuarioDaAreaRestrita = undefined
                    } else {
                        payload.usuarioDaAreaRestrita = undefined;
                    }

                    atualizarEscola.mutate(payload);

                } else {
                    formularioDeDadosDeAcesso.validateFields()
                        .then((dadosDeAcesso) => {
                            if (valoresDoFormularioDeDadosBasicos.pessoa?.cnpj == undefined) return;

                            if (props.privilegio == "administrador" && PERMITIR_CRIACAO_DE_USUARIO_DA_AREA_RESTRITA) {
                                dadosDeAcesso.usuario = Formatacoes.sanitizar(Sanitizacoes.CNPJ, valoresDoFormularioDeDadosBasicos?.pessoa?.cnpj?.trim())!;
                                payload.usuarioDaAreaRestrita = { ...dadosDeAcesso }
                            }

                            inserirEscola.mutate(payload);
                        })
                        .catch(() => {
                            scrollarParaOElemento(document.getElementById("inicio-do-formulario-de-dados-de-acesso-da-escola"));
                        })
                }

            })
            .catch(() => {
                scrollarParaOElemento(document.getElementById("inicio-do-formulario-de-dados-basicos-da-escola"));
            })
    }

    if(props.privilegio == "escola" && estaEditando == false) return <Empty description="Escolas não podem inserir novas escolas"/>

    return <React.Fragment>
        <Spin spinning={props.escolaEmAlteracao != undefined && configurouAsDependenciasDeEdicao == false}>
            <div>
                {
                    props.privilegio == "administrador" &&
                    estaEditando && props.escolaEmAlteracao?.usuarioDaAreaRestrita?.codigo != undefined &&
                    PERMITIR_CRIACAO_DE_USUARIO_DA_AREA_RESTRITA &&
                    <Row>
                        <Col xs={24} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                            <React.Fragment>
                                Usuário vinculado: <Button type="link" href={`/nao-implementado/#`} style={{ paddingLeft: "0.5em" }}>{props.escolaEmAlteracao?.usuarioDaAreaRestrita?.usuario}</Button>
                            </React.Fragment>
                        </Col>
                    </Row>
                }

                <span id="inicio-do-formulario-de-dados-basicos-da-escola" />
                <Col span={24}>
                    <h3 style={{ marginTop: 0 }}>
                        Dados básicos <QuestionCircleTwoTone id={"Not implemented yet"} style={{ cursor: "pointer" }} onClick={() => { }} />
                    </h3>
                </Col>

                <FormularioDeDadosBasicosDaEscola form={formularioDeDadosBasicos} privilegio={props.privilegio} />

                <Divider />

                <Col span={24}>
                    <h3 style={{ marginTop: 0 }}>
                        Endereços <QuestionCircleTwoTone id={"Not implemented yet"} style={{ cursor: "pointer" }} onClick={() => { }} />
                    </h3>
                </Col>

                <FormularioDeEnderecos enderecosAPI={[enderecos, setEnderecos]} permitirEdicaoDosEnderecos />

                <Divider />

                <Col span={24}>
                    <h3 style={{ marginTop: 0 }}>
                        Contatos <QuestionCircleTwoTone id={"Not implemented yet"} style={{ cursor: "pointer" }} onClick={() => { }} />
                    </h3>
                </Col>

                <FormularioDeContatos emailsAPI={[emails, setEmails]} telefonesAPI={[telefones, setTelefones]} permitirEdicaoDosEmails permitirEdicaoDosTelefones
                    ocultarDivisores
                />

                <Divider />

                {
                    props.privilegio == "administrador" && !estaEditando &&
                    PERMITIR_CRIACAO_DE_USUARIO_DA_AREA_RESTRITA &&
                    <React.Fragment>
                        <span id="inicio-do-formulario-de-dados-de-acesso-da-escola" />
                        <Col span={24}>
                            <h3 style={{ marginTop: 0 }}>
                                Dados de acesso <Tooltip title={<>Representa o usuário da área restrita a ser criado para a escola  </>}>
                                    <QuestionCircleTwoTone />
                                </Tooltip>
                            </h3>
                        </Col>

                        <Form<TypeDadosDeAcesso> form={formularioDeDadosDeAcesso} layout="vertical" autoComplete="off">
                            <Row gutter={16}>

                                <Col xs={24} md={8} lg={4}>
                                    <Form.Item label="Usuário">
                                        <Tooltip title="Por padrão, o usuário é sempre o CNPJ fornecido." placement="bottomLeft">
                                            <Input value={Formatacoes.sanitizar(Sanitizacoes.CNPJ, valoresDoFormularioDeDadosBasicos?.pessoa?.cnpj?.trim() ?? "")} disabled />
                                        </Tooltip>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12} lg={8} >
                                    <Form.Item<TypeDadosDeAcesso>
                                        name="senha" label="Senha inicial"
                                        rules={[
                                            { required: true, message: 'Por favor, informe a senha inicia!' },
                                            { min: 6, message: "A senha deve ter ao menos 6 caracteres" }
                                        ]}
                                    >
                                        <Input.Password
                                            placeholder="Informe a senha inicial"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </React.Fragment>
                }

                <Divider />

                <Row >
                    <Col span={24}>
                        <Flex align="center" justify="center" gap={16} >
                            {props.aoCancelar &&
                                <Button danger type="primary" key={"cancel"} onClick={() => { props.aoCancelar && props.aoCancelar() }}>
                                    Cancelar
                                </Button>
                            }
                            <Button type="primary" key={"submit"}
                                disabled={inserirEscola.isPending || atualizarEscola.isPending}
                                onClick={aoFinalizar}
                            >
                                Salvar
                            </Button>
                        </Flex>
                    </Col>
                </Row>

            </div>
        </Spin>
    </React.Fragment>

}
